import { BigNumber } from 'bignumber.js';
import { useCallback } from 'react';
import {
  BookkeepingConfiguration,
  useBookkeepingConfiguration,
} from '~/services/bookkeeping/bookkeeping-configuration';
import { useVATCodes } from '~/services/bookkeeping/vat-codes';
import { Counterpart } from '~/types/counterparts';
import { Document, DocumentDirection, DocumentType, DocumentVATCollection } from '~/types/document';
import {
  BookkeepingAccountSection,
  BookkeepingAccountType,
  BookkeepingEntry,
  BookkeepingEntrySource,
  CreateBookkeepingEntry,
} from '~/types/general-bookkeeping';
import { DettaglioLinea, ItalianEInvoice, isFSM10EInvoice } from '~/types/invoicing';
import { Money } from '~/types/money';
import { isDefined, assertIsDefined, assertIsNotEmpty } from '~/types/typeGuards';
import { VATBookType, VATCode, VATProposalItemType, VATRegistration } from '~/types/vat-bookkeeping';
import { isReverseChargeVatCode, vatBookForDirection, vatBookForDocument } from '~/utils/bookkeeping/registrations';
import { calculateDocumentTotals } from '~/utils/einvoice';
import {
  ensureDecimalPlaces,
  isNotZero,
  maybeSumMoney,
  negateMoney,
  newMoney,
  subtractMoney,
  sumMoney,
} from '~/utils/money';
import { VATEntryForm, VATEntryItem } from '~/views/bookkeeping/VAT-registration/VATRegistrationSchema';
import { calcDeductibleAmount, calcIndeductibleAmount } from './vat-code';
import { getEInvoiceBody } from '../invoicing';

interface BookkeepingTransactionForVATParams {
  document: Document | null;
  direction: DocumentDirection;
  vatEntries: VATEntryForm[];
  hasVatRegistrationException?: boolean;
  bookkeepingAccountId?: string | null;
  counterpartId?: string | null;
}

type BookkeepingEntryWithoutId = Omit<CreateBookkeepingEntry, 'lid'>;

/**
 * Hook that generates a complete bookkeeping transaction for a VAT registration.
 * Creates entries for:
 * 1. Supplier/Client account (based on document direction)
 * 2. Purchases/Sales VAT account (based on document direction)
 * 3. Individual expense/revenue accounts (for each VAT entry)
 */
export const useBookkeepingEntriesForVATRegistration = () => {
  const configuration = useBookkeepingConfiguration();
  const { data: vatCodes } = useVATCodes();

  return useCallback(
    ({
      document,
      direction,
      hasVatRegistrationException,
      vatEntries,
      bookkeepingAccountId,
      counterpartId,
    }: BookkeepingTransactionForVATParams): BookkeepingEntryWithoutId[] => {
      const vatBookType = document
        ? vatBookForDocument({ document, reverseCharge: hasVatRegistrationException })
        : vatBookForDirection(direction);
      const isCreditNote = document?.documentType === DocumentType.CreditNote;

      if (hasVatRegistrationException && document) {
        return generateSelfInvoicesEntries({
          document,
          vatBookType,
          vatEntries,
          vatCodes,
          configuration,
        });
      }

      if (document?.vatCollection === DocumentVATCollection.Split && vatBookType === VATBookType.Sales) {
        const splitPaymentEntries = generateSplitPaymentSalesEntries({
          document,
          vatEntries,
          vatCodes,
          configuration,
        });

        return isCreditNote ? negateEntries(splitPaymentEntries) : splitPaymentEntries;
      }

      const supplierClientEntry = generateSupplierClientEntry({
        vatBookType,
        vatEntries,
        configuration,
        document,
        bookkeepingAccountIdOverride: bookkeepingAccountId,
        counterpartIdOverride: counterpartId,
      });

      const expenseRevenueEntries = generateExpenseRevenueEntries({
        vatBookType,
        vatEntries,
        vatCodes,
        configuration,
      });

      return isCreditNote
        ? negateEntries([supplierClientEntry, ...expenseRevenueEntries])
        : [supplierClientEntry, ...expenseRevenueEntries];
    },
    [configuration, vatCodes],
  );
};

export const negateEntries = (entries: BookkeepingEntryWithoutId[]): BookkeepingEntryWithoutId[] => {
  return entries.map((entry) => ({
    ...entry,
    amount: negateMoney(entry.amount),
  }));
};

const calculateMainEntryAmount = ({
  vatBookType,
  vatEntries,
  hasVatRegistrationException,
}: {
  vatBookType: VATBookType;
  vatEntries: VATEntryForm[];
  hasVatRegistrationException: boolean;
}) => {
  const taxableAmount = vatEntries.reduce((acc, entry) => sumMoney(acc, entry.taxableAmount), newMoney(0));

  if (hasVatRegistrationException) {
    return taxableAmount;
  }

  const taxAmount = vatEntries.reduce((acc, entry) => sumMoney(acc, entry.taxAmount), newMoney(0));

  const total = sumMoney(taxableAmount, taxAmount);

  return vatBookType === VATBookType.Sales ? negateMoney(total) : total;
};

const getSupplierClientAccountIdFromEntries = (
  vatBookType: VATBookType,
  configuration: BookkeepingConfiguration,
  bookkeepingEntries: BookkeepingEntry[] | undefined,
) => {
  if (!bookkeepingEntries) return null;

  const vatAccountIds = new Set(
    [
      configuration.accounts.vatPurchases,
      configuration.accounts.vatSales,
      configuration.accounts.vatSalesSplitPayment,
    ].filter(isDefined),
  );

  const supplierClientCandidates = bookkeepingEntries.filter(
    (entry) =>
      entry.bookkeepingAccount.accountType === BookkeepingAccountType.SP &&
      !vatAccountIds.has(entry.bookkeepingAccount.id),
  );

  const targetSection =
    vatBookType === VATBookType.Purchases ? BookkeepingAccountSection.Passivo : BookkeepingAccountSection.Attivo;

  const supplierClientEntry =
    supplierClientCandidates.find((entry) => entry.bookkeepingAccount.section === targetSection) ??
    supplierClientCandidates[0];

  return supplierClientEntry?.bookkeepingAccount.id ?? null;
};

const getSupplierClientAccountIdFromCounterpart = (bookkeepingEntries: BookkeepingEntry[] | undefined) => {
  if (!bookkeepingEntries) return null;

  const counterpartEntry = bookkeepingEntries.find((entry) => isDefined(entry.counterpart));
  return counterpartEntry?.bookkeepingAccount.id ?? null;
};

const getSupplierClientAccountIdForDirection = (
  vatBookType: VATBookType,
  configuration: BookkeepingConfiguration,
  counterpart?: Counterpart,
) => {
  const { debtsSuppliers, debtsForeignSuppliers, creditsClients, creditsForeignClients } = configuration.accounts;
  const isCounterpartForeign = counterpart && counterpart.country !== 'IT';

  switch (vatBookType) {
    case VATBookType.Purchases:
      if (isCounterpartForeign) return debtsForeignSuppliers ?? debtsSuppliers ?? null;
      return debtsSuppliers ?? null;
    case VATBookType.Sales:
      if (isCounterpartForeign) return creditsForeignClients ?? creditsClients ?? null;
      return creditsClients ?? null;
  }
};

export const resolveSupplierClientAccountId = (
  vatBookType: VATBookType,
  configuration: BookkeepingConfiguration,
  counterpart?: Counterpart,
  bookkeepingEntries?: BookkeepingEntry[],
): string | null => {
  const accountIdFromCounterPart = getSupplierClientAccountIdFromCounterpart(bookkeepingEntries);
  if (accountIdFromCounterPart) return accountIdFromCounterPart;

  const accountIdFromEntries = getSupplierClientAccountIdFromEntries(vatBookType, configuration, bookkeepingEntries);
  if (accountIdFromEntries) return accountIdFromEntries;

  const accountIdFromContext = getSupplierClientAccountIdForDirection(vatBookType, configuration, counterpart);
  if (accountIdFromContext) return accountIdFromContext;

  return null;
};

const generateSupplierClientEntry = ({
  vatBookType,
  vatEntries,
  configuration,
  document,
  bookkeepingAccountIdOverride,
  counterpartIdOverride,
}: {
  vatBookType: VATBookType;
  vatEntries: VATEntryForm[];
  configuration: BookkeepingConfiguration;
  document: Document | null;
  bookkeepingAccountIdOverride?: string | null;
  counterpartIdOverride?: string | null;
}): BookkeepingEntryWithoutId => {
  const bookkeepingAccountId =
    bookkeepingAccountIdOverride ?? resolveSupplierClientAccountId(vatBookType, configuration, document?.counterpart);

  assertIsNotEmpty(bookkeepingAccountId, 'Bookkeeping account ID must be defined');

  return {
    bookkeepingAccountId,
    amount: calculateMainEntryAmount({ vatBookType, vatEntries, hasVatRegistrationException: false }),
    counterpartId: counterpartIdOverride ?? document?.counterpart?.id,
    documentId: document?.id,
    source: BookkeepingEntrySource.SETTINGS,
  };
};

const addOrAccumulateEntry = ({
  entries,
  bookkeepingAccountId,
  amount,
}: {
  entries: BookkeepingEntryWithoutId[];
  bookkeepingAccountId: string;
  amount: Money;
}) => {
  const existingEntry = entries.find((entry) => entry.bookkeepingAccountId === bookkeepingAccountId);

  if (existingEntry) {
    existingEntry.amount = sumMoney(existingEntry.amount, amount);
  } else {
    entries.push({ bookkeepingAccountId, amount, source: BookkeepingEntrySource.SETTINGS });
  }
};

const resolveVatAccountAndAmounts = ({
  vatBookType,
  configuration,
  entryAmount,
  vatEntryAmount,
}: {
  vatBookType: VATBookType;
  configuration: BookkeepingConfiguration;
  entryAmount: Money;
  vatEntryAmount: Money;
}): {
  vatBookkeepingAccountId: string;
  entryAmount: Money;
  vatEntryAmount: Money;
} => {
  let vatBookkeepingAccountId: string | null = null;

  switch (vatBookType) {
    case VATBookType.Purchases:
      vatBookkeepingAccountId = configuration.accounts.vatPurchases;
      entryAmount = negateMoney(entryAmount);
      vatEntryAmount = negateMoney(vatEntryAmount);
      break;

    case VATBookType.Sales:
      vatBookkeepingAccountId = configuration.accounts.vatSales;
      break;
  }

  assertIsNotEmpty(vatBookkeepingAccountId, 'Bookkeeping account ID must be defined');

  return {
    vatBookkeepingAccountId,
    entryAmount,
    vatEntryAmount,
  };
};

const generateSelfInvoiceExceptionEntries = ({
  configuration,
  vatBookType,
  result,
  vatEntry,
  vatCode,
  applyReverseCharge,
}: {
  vatBookType: VATBookType;
  vatEntry: VATEntryForm;
  vatCode: VATCode;
  configuration: BookkeepingConfiguration;
  result: BookkeepingEntryWithoutId[];
  applyReverseCharge: boolean;
}): BookkeepingEntryWithoutId[] => {
  const entryAmount = sumMoney(vatEntry.taxableAmount, calcIndeductibleAmount(vatEntry.taxAmount, vatCode));
  const vatEntryAmount = calcDeductibleAmount(vatEntry.taxAmount, vatCode);

  const {
    vatBookkeepingAccountId,
    entryAmount: signedEntryAmount,
    vatEntryAmount: signedVatEntryAmount,
  } = resolveVatAccountAndAmounts({ vatBookType, configuration, entryAmount, vatEntryAmount });

  addOrAccumulateEntry({
    entries: result,
    bookkeepingAccountId: vatEntry.bookkeepingAccountId,
    amount: negateMoney(signedEntryAmount),
  });

  if (!isNotZero(vatEntry.taxAmount)) {
    return result;
  }

  // VAT a debito: only for reverse-charge lines (natura N6.x)
  if (applyReverseCharge) {
    addOrAccumulateEntry({
      entries: result,
      bookkeepingAccountId: vatBookkeepingAccountId,
      amount: vatEntry.taxAmount,
    });
  }

  // VAT purchases (c/credito): only the deductible portion
  if (isNotZero(signedVatEntryAmount)) {
    const selfinvoiceAdditionalVat = configuration.accounts.vatPurchases;

    assertIsNotEmpty(selfinvoiceAdditionalVat, 'Bookkeeping account ID must be defined');

    addOrAccumulateEntry({
      entries: result,
      bookkeepingAccountId: selfinvoiceAdditionalVat,
      amount: negateMoney(signedVatEntryAmount),
    });
  }

  return result;
};

const generateExpenseRevenueEntries = ({
  vatBookType,
  vatEntries,
  vatCodes,
  configuration,
}: {
  vatBookType: VATBookType;
  vatEntries: VATEntryForm[];
  vatCodes: VATCode[];
  configuration: BookkeepingConfiguration;
}): BookkeepingEntryWithoutId[] => {
  return vatEntries.reduce((result: BookkeepingEntryWithoutId[], vatEntry) => {
    const vatCode = vatCodes.find((code) => code.id === vatEntry.vatCodeId);
    assertIsDefined(vatCode, `VAT code with ID ${vatEntry.vatCodeId} not found`);
    const entryAmount = sumMoney(vatEntry.taxableAmount, calcIndeductibleAmount(vatEntry.taxAmount, vatCode));
    const vatEntryAmount = calcDeductibleAmount(vatEntry.taxAmount, vatCode);

    const {
      vatBookkeepingAccountId,
      entryAmount: signedEntryAmount,
      vatEntryAmount: signedVatEntryAmount,
    } = resolveVatAccountAndAmounts({ vatBookType, configuration, entryAmount, vatEntryAmount });

    addOrAccumulateEntry({
      entries: result,
      bookkeepingAccountId: vatEntry.bookkeepingAccountId,
      amount: signedEntryAmount,
    });

    if (!isNotZero(signedVatEntryAmount)) {
      return result;
    }

    addOrAccumulateEntry({
      entries: result,
      bookkeepingAccountId: vatBookkeepingAccountId,
      amount: signedVatEntryAmount,
    });
    return result;
  }, []);
};

const generateSelfInvoicesEntries = ({
  document,
  vatBookType,
  vatEntries,
  vatCodes,
  configuration,
}: {
  document: Document;
  vatBookType: VATBookType;
  vatEntries: VATEntryForm[];
  vatCodes: VATCode[];
  configuration: BookkeepingConfiguration;
}): BookkeepingEntryWithoutId[] => {
  const isCounterpartForeign = document.counterpart?.country !== 'IT';
  const bookkeepingAccountId = isCounterpartForeign
    ? (configuration.accounts.debtsForeignSuppliers ?? configuration.accounts.debtsSuppliers)
    : configuration.accounts.debtsSuppliers;

  assertIsNotEmpty(bookkeepingAccountId, 'Bookkeeping account ID must be defined');

  const isSelfInvoice = document.documentType === DocumentType.SelfInvoice;
  const isLineReverseCharge = (entry: VATEntryForm): boolean =>
    isSelfInvoice || isReverseChargeVatCode(vatCodes.find((code) => code.id === entry.vatCodeId));

  const supplierDebtAmount = vatEntries.reduce(
    (acc, entry) =>
      sumMoney(acc, isLineReverseCharge(entry) ? entry.taxableAmount : sumMoney(entry.taxableAmount, entry.taxAmount)),
    newMoney(0),
  );

  const mainAmount: BookkeepingEntryWithoutId = {
    bookkeepingAccountId,
    amount: supplierDebtAmount,
    counterpartId: document.counterpart?.id,
    documentId: document.id,
    source: BookkeepingEntrySource.SETTINGS,
  };

  const otherEntries = vatEntries.reduce((result: BookkeepingEntryWithoutId[], vatEntry) => {
    const vatCode = vatCodes.find((code) => code.id === vatEntry.vatCodeId);
    assertIsDefined(vatCode, `VAT code with ID ${vatEntry.vatCodeId} not found`);
    return generateSelfInvoiceExceptionEntries({
      vatEntry,
      vatCode,
      vatBookType,
      configuration,
      result,
      applyReverseCharge: isSelfInvoice || isReverseChargeVatCode(vatCode),
    });
  }, []);

  return [mainAmount, ...otherEntries];
};

/**
 * Generates bookkeeping entries for split payment SALES invoices (art. 17-ter DPR 633/1972).
 *
 * For split payment sales, the supplier does NOT collect VAT from the customer.
 * The customer (public administration) will pay VAT directly to the tax authority.
 *
 * Structure:
 * - Customer receivable: NET amount (taxableAmount only)
 * - Revenue accounts: taxableAmount (one or more entries)
 * - VAT split payment account: BOTH debit and credit (makes VAT neutral)
 *
 * Example (taxable 1,000 + VAT 220):
 *   - DARE (debit):  Customer -1,000, VAT split -220
 *   - AVERE (credit): Revenue +1,000, VAT split +220
 */
const generateSplitPaymentSalesEntries = ({
  document,
  vatEntries,
  vatCodes,
  configuration,
}: {
  document: Document;
  vatEntries: VATEntryForm[];
  vatCodes: VATCode[];
  configuration: BookkeepingConfiguration;
}): BookkeepingEntryWithoutId[] => {
  const totalTaxableAmount = vatEntries.reduce(
    (acc, entry) => sumMoney(acc, entry.taxableAmount),
    newMoney(0, document.grossAmount.currency),
  );

  const totalVATAmount = vatEntries.reduce(
    (acc, entry) => {
      const vatCode = vatCodes.find((code) => code.id === entry.vatCodeId);
      assertIsDefined(vatCode, `VAT code with ID ${entry.vatCodeId} not found`);
      const deductibleVat = calcDeductibleAmount(entry.taxAmount, vatCode);
      return sumMoney(acc, deductibleVat);
    },
    newMoney(0, document.grossAmount.currency),
  );

  const result: BookkeepingEntryWithoutId[] = [];

  // 1. Customer receivable entry (NET amount)
  const clientsAccountId = configuration.accounts.creditsClients;
  assertIsNotEmpty(clientsAccountId, 'Clients account not configured');

  result.push({
    bookkeepingAccountId: clientsAccountId,
    amount: negateMoney(totalTaxableAmount),
    counterpartId: document.counterpart?.id,
    documentId: document.id,
    source: BookkeepingEntrySource.SETTINGS,
  });

  // 2. Revenue entries (one per VAT entry)
  vatEntries.forEach((vatEntry) => {
    const vatCode = vatCodes.find((code) => code.id === vatEntry.vatCodeId);
    assertIsDefined(vatCode, `VAT code with ID ${vatEntry.vatCodeId} not found`);

    const entryAmount = sumMoney(vatEntry.taxableAmount, calcIndeductibleAmount(vatEntry.taxAmount, vatCode));

    addOrAccumulateEntry({
      entries: result,
      bookkeepingAccountId: vatEntry.bookkeepingAccountId,
      amount: entryAmount,
    });
  });

  // 3. VAT split payment entries (BOTH debit and credit to make it neutral)
  // Note: We push these directly rather than using addOrAccumulateEntry
  // because we need BOTH entries to appear even though they use the same account
  if (isNotZero(totalVATAmount)) {
    const splitPaymentAccountId = configuration.accounts.vatSalesSplitPayment;
    assertIsNotEmpty(splitPaymentAccountId, 'Split Payment VAT account not configured');

    // Debit side (dare) - negative amount
    result.push({
      bookkeepingAccountId: splitPaymentAccountId,
      amount: negateMoney(totalVATAmount),
      source: BookkeepingEntrySource.SETTINGS,
    });

    // Credit side (avere) - positive amount
    result.push({
      bookkeepingAccountId: splitPaymentAccountId,
      amount: totalVATAmount,
      source: BookkeepingEntrySource.SETTINGS,
    });
  }

  return result;
};

export const calculateTaxAmountFromTaxable = (taxableAmount: string, vatPercentage: string): string => {
  const taxable = Number(taxableAmount);
  const percentage = Number(vatPercentage);

  const taxAmount = taxable * (percentage / 100);
  return taxAmount.toString();
};

export const generateVatGroupItemFromLineDetails = (item: DettaglioLinea): VATEntryItem => {
  return {
    lineNumber: item.numeroLinea,
    description: item.descrizione,
    taxableAmount: {
      amount: item.prezzoTotale,
      currency: 'EUR',
    },
    taxAmount: {
      amount: calculateTaxAmountFromTaxable(item.prezzoTotale, item.aliquotaIva),
      currency: 'EUR',
    },
    nature: item.natura || null,
    vatPercentage: item.aliquotaIva,
    itemProposalId: null,
    itemType: VATProposalItemType.Product,
  };
};

export const calculateAmountsFromEInvoice = (
  eInvoice: ItalianEInvoice,
  number: string,
): { taxableAmount: Money; taxAmount: Money; grossAmount: Money } | null => {
  // FSM10 (simplified invoices) have no datiRiepilogo to compute amounts from.
  if (isFSM10EInvoice(eInvoice)) {
    return null;
  }

  const body = getEInvoiceBody(eInvoice, number);
  const totals = calculateDocumentTotals(body);

  return {
    taxableAmount: ensureDecimalPlaces(totals.taxableTotal, 2),
    taxAmount: ensureDecimalPlaces(totals.vatTotal, 2),
    grossAmount: ensureDecimalPlaces(totals.grossTotal, 2),
  };
};

export const getRegistrationBalancing = (
  document: Document | null,
  entries: VATEntryForm[],
  eInvoice?: ItalianEInvoice | null,
) => {
  const entriesTaxableAmount = entries.reduce((acc, entry) => maybeSumMoney(acc, entry.taxableAmount)!, newMoney(0));
  const entriesTaxAmount = entries.reduce((acc, entry) => maybeSumMoney(acc, entry.taxAmount)!, newMoney(0));
  const entriesGrossAmount = maybeSumMoney(entriesTaxableAmount, entriesTaxAmount)!;

  if (!document && !eInvoice) {
    return {
      isBalanced: true,
      expectedTaxableAmount: entriesTaxableAmount,
      entriesTaxableAmount,
      taxableBalanced: true,
      taxableDifference: newMoney(0),
      expectedTaxAmount: entriesTaxAmount,
      entriesTaxAmount,
      taxBalanced: true,
      taxDifference: newMoney(0),
      expectedGrossAmount: entriesGrossAmount,
      entriesGrossAmount,
      grossBalanced: true,
      grossDifference: newMoney(0),
    };
  }

  let expectedTaxableAmount: Money;
  let expectedTaxAmount: Money;
  let expectedGrossAmount: Money;

  const eInvoiceAmounts = document && eInvoice ? calculateAmountsFromEInvoice(eInvoice, document.number!) : null;
  if (eInvoiceAmounts) {
    expectedTaxableAmount = eInvoiceAmounts.taxableAmount;
    expectedTaxAmount = eInvoiceAmounts.taxAmount;
    expectedGrossAmount = eInvoiceAmounts.grossAmount;
  } else {
    expectedTaxableAmount = subtractMoney(document!.grossAmount, document!.vatAmount);
    expectedTaxAmount = document!.vatAmount;
    expectedGrossAmount = document!.grossAmount;
  }

  //forcing document amounts to EUR curency to avoid calculation error. this is just an interim solution
  //to allow the accountant to register the document. in a complete solution, we should convert the amount
  //to EUR based on the date of the document
  const taxableDifference = subtractMoney(entriesTaxableAmount, newMoney(expectedTaxableAmount.amount, 'EUR'));
  const taxableBalanced = BigNumber(taxableDifference.amount).isZero();

  const taxDifference = subtractMoney(entriesTaxAmount, newMoney(expectedTaxAmount.amount, 'EUR'));
  const taxBalanced = BigNumber(taxDifference.amount).isZero();

  const grossDifference = subtractMoney(entriesGrossAmount, newMoney(expectedGrossAmount.amount, 'EUR'));
  const grossBalanced = BigNumber(grossDifference.amount).isZero();

  const isBalanced = document?.subjectToReverseCharge
    ? taxableBalanced
    : taxableBalanced && taxBalanced && grossBalanced;

  return {
    isBalanced,
    expectedTaxableAmount,
    entriesTaxableAmount,
    taxableBalanced,
    taxableDifference,
    expectedTaxAmount,
    entriesTaxAmount,
    taxBalanced,
    taxDifference,
    expectedGrossAmount,
    entriesGrossAmount,
    grossBalanced,
    grossDifference,
  };
};

export const getPrimaryRegistration = (registrations: VATRegistration[]) =>
  registrations.length === 2
    ? registrations.find((registration) => registration.vatBook.bookType === VATBookType.Sales)
    : registrations[0];

export const getSecondaryRegistration = (registrations: VATRegistration[]) =>
  registrations.length === 2
    ? registrations.find((registration) => registration.vatBook.bookType === VATBookType.Purchases)
    : undefined;
